import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ShowerHead, Container, PlugZap, MessageCircle, Plus } from "lucide-react";

// Map icon slugs (returned by backend) to lucide components
const ICONS = {
  "shower-head": ShowerHead,
  "container": Container,
  "plug-zap": PlugZap,
};

export default function LotBooking() {
  const { lotNumber } = useParams();
  const navigate = useNavigate();
  const [lot, setLot] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedSvc, setSelectedSvc] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    tenant_name: "",
    tenant_email: "",
    tenant_phone: "",
    company: "",
    intended_use: "Storage / Parking",
    lease_term_months: 12,
    start_date: new Date().toISOString().slice(0, 10),
  });

  useEffect(() => {
    Promise.all([
      api.get(`/lots/${lotNumber}`),
      api.get("/services"),
    ]).then(([l, s]) => {
      setLot(l.data);
      setServices(s.data);
    }).catch(() => {
      toast.error("Lot not found");
      navigate("/");
    });
  }, [lotNumber, navigate]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (id) => {
    setSelectedSvc((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!lot) return;
    if (lot.status !== "available") return toast.error("This lot is no longer available");
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        ...form, lot_number: lot.lot_number,
        service_ids: Array.from(selectedSvc),
      });
      const { data: session } = await api.post("/checkout/session", {
        booking_id: booking.id, origin_url: window.location.origin,
      });
      window.location.href = session.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
      setSubmitting(false);
    }
  };

  if (!lot) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0B0C0E]" /></div>;
  }

  // Compute totals
  const selectedMonthlyServices = services.filter(
    (s) => selectedSvc.has(s.id) && s.billing_type === "monthly" && s.price_per_month
  );
  const selectedDiscussServices = services.filter(
    (s) => selectedSvc.has(s.id) && s.billing_type === "on_request"
  );
  const svcMonthlyTotal = selectedMonthlyServices.reduce((a, s) => a + s.price_per_month, 0);
  const total = lot.monthly_rent + 500 + svcMonthlyTotal;

  return (
    <div data-testid="lot-booking-page" className="min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <button onClick={() => navigate("/")} className="text-[#56606E] hover:text-[#0B0C0E] inline-flex items-center gap-2 text-sm mb-8" data-testid="back-to-plan">
          <ArrowLeft className="w-4 h-4" /> Back to site plan
        </button>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Lot summary */}
          <aside className="lg:col-span-2 self-start border-2 border-[#0B0C0E] bg-white p-8 sticky top-20">
            <div className="overline">Selected</div>
            <div className="mt-2 font-display font-black text-6xl text-[#0B0C0E]">Lot {lot.lot_number}</div>
            <div className="mt-2 text-[#56606E] text-sm">{lot.sq_ft.toLocaleString()} sq ft · {lot.acres.toFixed(2)} acres</div>

            <div className="mt-8 grid grid-cols-2 gap-px bg-[#E5E3DA] border border-[#E5E3DA] text-sm">
              <Row label="Monthly Rent" value={fmtMoney(lot.monthly_rent)} highlight />
              <Row label="Security Deposit" value={fmtMoney(500)} />
              <Row label="Term" value={`${form.lease_term_months} months`} />
              <Row label="Status" value={lot.status.toUpperCase()} />
            </div>

            {/* Selected add-ons summary */}
            {(selectedMonthlyServices.length > 0 || selectedDiscussServices.length > 0) && (
              <div className="mt-6 border-t border-[#E5E3DA] pt-4" data-testid="addons-summary">
                <div className="overline mb-2">Add-ons</div>
                {selectedMonthlyServices.map((s) => (
                  <div key={s.id} className="flex justify-between text-sm py-1 text-[#0B0C0E]">
                    <span>{s.name}</span>
                    <span className="font-mono">+{fmtMoney(s.price_per_month)}/mo</span>
                  </div>
                ))}
                {selectedDiscussServices.map((s) => (
                  <div key={s.id} className="flex justify-between text-sm py-1 text-amber-700">
                    <span>{s.name}</span>
                    <span className="italic text-xs">— quote separately</span>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 border-t border-[#E5E3DA] pt-6 flex items-baseline justify-between">
              <span className="overline">Due Today</span>
              <span className="font-display font-black text-3xl text-[#0B0C0E] bg-[#FACC15] px-3" data-testid="total-due">{fmtMoney(total)}</span>
            </div>
            <p className="mt-4 text-xs text-[#56606E] leading-relaxed">
              First month + $500 deposit{svcMonthlyTotal > 0 ? ` + first month of add-ons (${fmtMoney(svcMonthlyTotal)})` : ""}.
              {selectedDiscussServices.length > 0 && " We'll contact you within 1 business day to discuss the marked-up services."}
            </p>
          </aside>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-3" data-testid="booking-form">
            <div className="overline">Tenant Details</div>
            <h1 className="mt-2 font-display font-black text-4xl uppercase tracking-tight text-[#0B0C0E]">Book Lot {lot.lot_number}</h1>
            <p className="mt-3 text-[#56606E] text-sm">All fields are used on your lease agreement. Make sure they&apos;re accurate.</p>

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              <Field label="Full Name *" required value={form.tenant_name} onChange={(v) => setField("tenant_name", v)} testId="input-tenant-name" />
              <Field label="Company (optional)" value={form.company} onChange={(v) => setField("company", v)} testId="input-company" />
              <Field label="Email *" type="email" required value={form.tenant_email} onChange={(v) => setField("tenant_email", v)} testId="input-email" />
              <Field label="Phone *" type="tel" required value={form.tenant_phone} onChange={(v) => setField("tenant_phone", v)} testId="input-phone" />
              <Field label="Start Date *" type="date" required value={form.start_date} onChange={(v) => setField("start_date", v)} testId="input-start-date" />
              <div>
                <label className="overline mb-2 block">Lease Term *</label>
                <select
                  value={form.lease_term_months}
                  onChange={(e) => setField("lease_term_months", parseInt(e.target.value))}
                  className="w-full bg-white border border-[#C6C3B5] hover:border-[#0B0C0E] focus:border-[#0B0C0E] outline-none px-4 py-3 text-[#0B0C0E] text-sm"
                  data-testid="input-term"
                >
                  <option value={6}>6 months</option>
                  <option value={12}>12 months</option>
                  <option value={24}>24 months</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <Field label="Intended Use" value={form.intended_use} onChange={(v) => setField("intended_use", v)} testId="input-use" />
              </div>
            </div>

            {/* Add-ons / Services */}
            {services.length > 0 && (
              <div className="mt-12" data-testid="services-section">
                <div className="overline">Add-on Services</div>
                <h2 className="mt-2 font-display font-black text-2xl uppercase tracking-tight text-[#0B0C0E]">Optional extras</h2>
                <p className="mt-2 text-sm text-[#56606E]">Add these to your monthly lease. Cancel any time with 30 days&apos; notice.</p>

                <div className="mt-6 grid gap-3">
                  {services.map((svc) => {
                    const Icon = ICONS[svc.icon] || Plus;
                    const checked = selectedSvc.has(svc.id);
                    const isDiscuss = svc.billing_type === "on_request";
                    return (
                      <label
                        key={svc.id}
                        className={`flex items-start gap-4 border-2 p-4 cursor-pointer transition ${
                          checked ? "border-[#0B0C0E] bg-[#FACC15]/15" : "border-[#E5E3DA] bg-white hover:border-[#0B0C0E]"
                        }`}
                        data-testid={`service-${svc.slug}`}
                      >
                        <input
                          type="checkbox" checked={checked} onChange={() => toggleService(svc.id)}
                          className="mt-1 w-4 h-4 accent-[#0B0C0E] flex-shrink-0"
                          data-testid={`service-checkbox-${svc.slug}`}
                        />
                        <div className="flex-shrink-0 w-10 h-10 bg-[#0B0C0E] text-[#FACC15] flex items-center justify-center">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-baseline justify-between gap-3 flex-wrap">
                            <div className="font-display font-black text-lg uppercase tracking-tight text-[#0B0C0E]">{svc.name}</div>
                            <div className={`font-bold ${isDiscuss ? "text-amber-700 text-sm" : "text-[#0B0C0E]"}`}>
                              {isDiscuss ? (
                                <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Let&apos;s discuss</span>
                              ) : (
                                <>{fmtMoney(svc.price_per_month)}<span className="text-xs font-medium text-[#56606E]">/mo</span></>
                              )}
                            </div>
                          </div>
                          <p className="mt-1 text-sm text-[#56606E]">{svc.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-10 flex items-center gap-4">
              <button
                type="submit"
                disabled={submitting || lot.status !== "available"}
                className="bg-[#0B0C0E] hover:bg-black disabled:opacity-50 text-white font-bold px-10 py-4 uppercase tracking-wide inline-flex items-center gap-3 border-l-4 border-[#FACC15]"
                data-testid="book-and-pay-button"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Continue to Payment ({fmtMoney(total)})</>}
              </button>
            </div>
            <p className="mt-4 text-xs text-[#56606E]">Secure payment by Stripe. You&apos;ll be redirected to complete checkout.</p>
          </form>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testId }) => (
  <div>
    <label className="overline mb-2 block">{label}</label>
    <input
      type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white border border-[#C6C3B5] hover:border-[#0B0C0E] focus:border-[#0B0C0E] outline-none px-4 py-3 text-[#0B0C0E] text-sm"
      data-testid={testId}
    />
  </div>
);

const Row = ({ label, value, highlight }) => (
  <div className="bg-white p-4">
    <div className="overline">{label}</div>
    <div className={`mt-1 font-medium ${highlight ? "text-[#0B0C0E] font-display font-black text-xl" : "text-[#2C2F36]"}`}>{value}</div>
  </div>
);
